/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetOutcomeChartRow, GetOutcomesRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { AmmUserActivitySchema, MarketTradeSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetOutcomeChart ────────────────────────────────────────────────────────────

export interface useGetOutcomeChartArgs { readonly outcomeMint: string; readonly start?: string; readonly end?: string; readonly timeframe?: '1s' | '5s' | '15s' | '30s' | '1m' | '5m' | '15m' | '30m' | '1h' | '2h' | '4h' | '1d' | '1w' | '1mo'; }


async function _useGetOutcomeChartQueryFn(
  args: useGetOutcomeChartArgs,
  ctx: QueryFunctionContext,
): Promise<GetOutcomeChartRow | null> {
  if (args.outcomeMint === undefined || args.outcomeMint === null || args.outcomeMint === '') return null;
  const url = `/v1/outcomes/${args.outcomeMint}/chart`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.start !== undefined) params.set('start', String(args.start));
  if (args.end !== undefined) params.set('end', String(args.end));
  if (args.timeframe !== undefined) params.set('timeframe', String(args.timeframe));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetOutcomeChart',
    routeTemplate: '/v1/outcomes/{outcomeMint}/chart',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetOutcomeChartRow;
}


const _useGetOutcomeChartFactory = Object.assign(
  (args: useGetOutcomeChartArgs) => ({
    queryKey: ['outcomes', 'chart', args.outcomeMint, { start: args.start ?? null, end: args.end ?? null, timeframe: args.timeframe ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetOutcomeChartQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetOutcomeChart',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['outcomes', 'chart', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetOutcomeChartArgs, GetOutcomeChartRow>;


export const useGetOutcomeChartSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/outcomes/{outcomeMint}/chart',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetOutcomeChartFactory },
  parseInput: (raw: unknown): useGetOutcomeChartArgs => raw as useGetOutcomeChartArgs,
  parseResponse: (raw: unknown): GetOutcomeChartRow => raw as GetOutcomeChartRow,
  specName: 'useGetOutcomeChart' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetOutcomeChartRow>;

function _useGetOutcomeChartPerEntitySubjects(args: useGetOutcomeChartArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.amm.${args.outcomeMint}.user.*.activity.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetOutcomeChartApplyEvent(
  prev: GetOutcomeChartRow | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetOutcomeChartArgs,
): GetOutcomeChartRow | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetOutcomeChartRow = prev;
    // subject "morfi.amm.${mint}.user.*.activity.v0"
  {
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
        next = (HELPERS.applyAmmActivityToOutcomeChart as unknown as (prev: GetOutcomeChartRow, raw: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOutcomeChartRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

export const useGetOutcomeChartRealtime = {
  domain: 'outcomes' as const,
  domainPins: [] as readonly string[],
  rowKey: 'outcomeMint' as const,
  perEntitySubjects: (args: useGetOutcomeChartArgs) => _useGetOutcomeChartPerEntitySubjects(args),
  viewportGated: false as const,
  applyImmediately: true as const,
  admitSequenceOnlyOnMutation: true as const,
  singleton: true as const,
  applyEvent:
    (args: useGetOutcomeChartArgs) =>
    (prev: GetOutcomeChartRow | undefined, ev: MorfiEvent<unknown>): GetOutcomeChartRow | undefined =>
      _useGetOutcomeChartApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetOutcomeChartRow, unknown>;


// ─── useGetOutcomes ────────────────────────────────────────────────────────────

export interface useGetOutcomesArgs { readonly marketPubkey?: string; readonly markets?: string; readonly mints?: string; readonly viewportGated?: boolean; readonly fields?: readonly ('pubkey' | 'mint' | 'market' | 'outcomeIndex' | 'label' | 'ticker' | 'uri' | 'claimRightsMint' | 'prizeRightsMint' | 'bondingCurve' | 'cpSwapPool' | 'marketStatus' | 'winnerOutcomeIndex' | 'imageUrl' | 'imageNsfw' | 'initialOutcomeMintSupplyD6' | 'indexedOutcomeMintSupplyD6' | 'claimRightsSupplyD6' | 'virtualQuoteD6' | 'latestPoolQuoteD6' | 'cpQuoteReserveD6' | 'cpOutcomeReserveD6' | 'migrated' | 'stateLastEventSlot' | 'stateLastEventTxIndex' | 'stateLastEventCpiIndex' | 'bcQuoteLastEventSlot' | 'bcQuoteLastEventTxIndex' | 'bcQuoteLastEventCpiIndex' | 'claimRightsLastEventSlot' | 'claimRightsLastEventTxIndex' | 'claimRightsLastEventCpiIndex' | 'price' | 'totalSupply' | 'reserves' | 'effectiveMarketCapD6' | 'conviction' | 'convictionAnchor' | 'convictionTarget' | 'graduated' | 'drained')[]; }


async function _useGetOutcomesQueryFn(
  args: useGetOutcomesArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetOutcomesRow>> {
  const url = '/v1/outcomes';
  const params = new URLSearchParams();
  const effectiveFields = args.fields === undefined ? undefined : [...new Set([...args.fields, 'mint'])].sort();
  if (effectiveFields !== undefined) params.set('fields', effectiveFields.join(','));
  if (args.marketPubkey !== undefined) params.set('marketPubkey', String(args.marketPubkey));
  if (args.markets !== undefined) params.set('markets', String(args.markets));
  if (args.mints !== undefined) params.set('mints', String(args.mints));
  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetOutcomes',
    routeTemplate: '/v1/outcomes',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetOutcomesRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetOutcomesFactory = Object.assign(
  (args: useGetOutcomesArgs) => ({
    queryKey: ['outcomes', 'list', { marketPubkey: args.marketPubkey ?? null, markets: args.markets ?? null, mints: args.mints ?? null }, ((args.fields === undefined ? undefined : [...new Set([...args.fields, 'mint'])].sort()) ?? [])] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetOutcomesQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetOutcomes',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['outcomes', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetOutcomesArgs, PagedData<GetOutcomesRow>>;


export const useGetOutcomesSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/outcomes',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetOutcomesFactory },
  parseInput: (raw: unknown): useGetOutcomesArgs => raw as useGetOutcomesArgs,
  parseResponse: (raw: unknown): PagedData<GetOutcomesRow> => raw as PagedData<GetOutcomesRow>,
  specName: 'useGetOutcomes' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['outcomes', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"outcomes","fieldMap":{"reserves":"virtualQuoteD6","totalSupply":"indexedOutcomeMintSupplyD6","migrated":"graduated"},"canonicalIdField":"pubkey"} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetOutcomesRow>>;
export const useGetOutcomesPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetOutcomesArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetOutcomesQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetOutcomesPerEntitySubjects(_args: useGetOutcomesArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0'];
  return [...new Set(_subs)];
}

function _useGetOutcomesRowSubjectTemplates(_args: useGetOutcomesArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.market.{market}.user.*.trade.v0', 'morfi.amm.{mint}.user.*.activity.v0'];
  return _tmpls;
}

function _useGetOutcomesApplyEvent(
  prev: PagedData<GetOutcomesRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetOutcomesArgs,
): PagedData<GetOutcomesRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.market.${m}.user.*.trade.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.market\.([^.]+)\.user\.[^.]+\.trade\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketTradeSchema, _d)
            : fromBinary(MarketTradeSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketTradeSchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetOutcomesRow;
        let updated: GetOutcomesRow = _rowOrig;
        updated = (HELPERS.applyMarketTradeToOutcome as unknown as (prev: GetOutcomesRow, raw: ReturnType<typeof fromBinary<typeof MarketTradeSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOutcomesRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // morfi.amm.${mint}.user.*.activity.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetOutcomesRow;
        let updated: GetOutcomesRow = _rowOrig;
        updated = (HELPERS.applyAmmActivityToOutcome as unknown as (prev: GetOutcomesRow, raw: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetOutcomesRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetOutcomesRealtime = {
  domain: 'outcomes' as const,
  domainPins: ['morfi.market.*.user.*.trade.v0', 'morfi.amm.*.user.*.activity.v0'] as readonly string[],
  rowKey: 'mint' as const,
  perEntitySubjects: (args: useGetOutcomesArgs) => _useGetOutcomesPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetOutcomesArgs) => _useGetOutcomesRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetOutcomesArgs) =>
    (prev: PagedData<GetOutcomesRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetOutcomesRow> | undefined =>
      _useGetOutcomesApplyEvent(prev, ev, _args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetOutcomesRow>, unknown>;
